import { useCallback, useState } from 'react';
import axios from 'axios';

/**
 * The refusal a sensitive action answers with when the session's assertion has gone stale
 * (`docs/WebAuthn.md` §4.6).
 *
 * ⚠ **403, never 401.** A 401 means "your session is invalid" and routes the client into a full
 * sign-in prompt; here the session is fine and only its freshness is not. A caller that treats
 * this as 401 signs the user out mid-action.
 */
export interface StepUp {
  stepUpRequired: 'step_up' | 'password';
  challengeId?: string;
  challenge?: { publicKey: Record<string, unknown> };
}

/** Pull a step-up refusal out of an axios error, if that is what it is. */
export function isStepUpRequired(e: unknown): StepUp | undefined {
  if (!axios.isAxiosError(e) || e.response?.status !== 403) return undefined;
  const body = e.response.data as StepUp | undefined;
  return typeof body?.stepUpRequired === 'string' ? body : undefined;
}

/** Holds the outstanding step-up, if any. */
export function useStepUp() {
  const [challenge, setChallenge] = useState<StepUp | undefined>(undefined);
  return {
    challenge,
    satisfy: useCallback((c: StepUp) => { setChallenge(c); }, []),
    clear: useCallback(() => { setChallenge(undefined); }, []),
  };
}
